/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getArrayDecoder,
  getArrayEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getOptionDecoder,
  getOptionEncoder,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type Option,
  type OptionOrNullable,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import { getAccountMetaFactory, type ResolvedAccount } from "../shared";

export const REGISTER_GHOST_METADATA_DISCRIMINATOR = new Uint8Array([
  34, 233, 111, 236, 212, 22, 26, 254,
]);

export function getRegisterGhostMetadataDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    REGISTER_GHOST_METADATA_DISCRIMINATOR,
  );
}

export type RegisterGhostMetadataInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountAgentAccount extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountAgentAccount extends string
        ? WritableAccount<TAccountAgentAccount>
        : TAccountAgentAccount,
      TAccountAuthority extends string
        ? WritableSignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      ...TRemainingAccounts,
    ]
  >;

export type RegisterGhostMetadataInstructionData = {
  discriminator: ReadonlyUint8Array;
  name: string;
  description: string;
  capabilities: Array<string>;
  serviceEndpoint: Option<string>;
};

export type RegisterGhostMetadataInstructionDataArgs = {
  name: string;
  description: string;
  capabilities: Array<string>;
  serviceEndpoint: OptionOrNullable<string>;
};

export function getRegisterGhostMetadataInstructionDataEncoder(): Encoder<RegisterGhostMetadataInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["name", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["description", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      [
        "capabilities",
        getArrayEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
      [
        "serviceEndpoint",
        getOptionEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
    ]),
    (value) => ({
      ...value,
      discriminator: REGISTER_GHOST_METADATA_DISCRIMINATOR,
    }),
  );
}

export function getRegisterGhostMetadataInstructionDataDecoder(): Decoder<RegisterGhostMetadataInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["name", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["description", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    [
      "capabilities",
      getArrayDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
    [
      "serviceEndpoint",
      getOptionDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
  ]);
}

export function getRegisterGhostMetadataInstructionDataCodec(): Codec<
  RegisterGhostMetadataInstructionDataArgs,
  RegisterGhostMetadataInstructionData
> {
  return combineCodec(
    getRegisterGhostMetadataInstructionDataEncoder(),
    getRegisterGhostMetadataInstructionDataDecoder(),
  );
}

export type RegisterGhostMetadataInput<
  TAccountAgentAccount extends string = string,
  TAccountAuthority extends string = string,
> = {
  agentAccount: Address<TAccountAgentAccount>;
  /**
   * Anyone can add metadata to an Unregistered Ghost
   * Claiming proves ownership later
   */
  authority: TransactionSigner<TAccountAuthority>;
  name: RegisterGhostMetadataInstructionDataArgs["name"];
  description: RegisterGhostMetadataInstructionDataArgs["description"];
  capabilities: RegisterGhostMetadataInstructionDataArgs["capabilities"];
  serviceEndpoint: RegisterGhostMetadataInstructionDataArgs["serviceEndpoint"];
};

export function getRegisterGhostMetadataInstruction<
  TAccountAgentAccount extends string,
  TAccountAuthority extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: RegisterGhostMetadataInput<TAccountAgentAccount, TAccountAuthority>,
  config?: { programAddress?: TProgramAddress },
): RegisterGhostMetadataInstruction<
  TProgramAddress,
  TAccountAgentAccount,
  TAccountAuthority
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    agentAccount: { value: input.agentAccount ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.agentAccount),
      getAccountMeta(accounts.authority),
    ],
    data: getRegisterGhostMetadataInstructionDataEncoder().encode(
      args as RegisterGhostMetadataInstructionDataArgs,
    ),
    programAddress,
  } as RegisterGhostMetadataInstruction<
    TProgramAddress,
    TAccountAgentAccount,
    TAccountAuthority
  >);
}

export type ParsedRegisterGhostMetadataInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    agentAccount: TAccountMetas[0];
    /**
     * Anyone can add metadata to an Unregistered Ghost
     * Claiming proves ownership later
     */
    authority: TAccountMetas[1];
  };
  data: RegisterGhostMetadataInstructionData;
};

export function parseRegisterGhostMetadataInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedRegisterGhostMetadataInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: { agentAccount: getNextAccount(), authority: getNextAccount() },
    data: getRegisterGhostMetadataInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
